import { useState, useEffect } from 'react';
import { getLocationById, getDisplayNameFromId } from '../utils/findLocation';

import { JOB_BOARD_SEARCH_HISTORY } from '../../../../../ApiUrl';
import { useCallAPI } from '../../../../../hooks/useCallAPI';

const PLATFORM = 'MONSTER';

export const useSearchHistory = () => {
  const [recentSearches, setRecentSearches] = useState({
    data: [],
    pagination: {
      total: 0,
      page: 1,
      limit: 10,
      pages: 1,
      hasMore: false
    }
  });
  const [bookmarkedSearches, setBookmarkedSearches] = useState([]);
  const [archivedSearches, setArchivedSearches] = useState([]);

  const recentSearchAPI = useCallAPI(JOB_BOARD_SEARCH_HISTORY);
  const bookmarkedSearchAPI = useCallAPI(JOB_BOARD_SEARCH_HISTORY);
  const archivedSearchAPI = useCallAPI(JOB_BOARD_SEARCH_HISTORY);
  const addSearchAPI = useCallAPI(JOB_BOARD_SEARCH_HISTORY);
  const deleteSearchAPI = useCallAPI(JOB_BOARD_SEARCH_HISTORY);
  const updateSearchAPI = useCallAPI(JOB_BOARD_SEARCH_HISTORY);
  const searchAPI = useCallAPI(JOB_BOARD_SEARCH_HISTORY + '/search');

  const transformSearchData = (data) => {
    if (!data || !Array.isArray(data)) return [];

    return data?.map(item => {
      const locationFromBackend = item.query?.location || '';
      
      let locationValue = locationFromBackend;
      
      if (typeof locationFromBackend === 'string' && /^\d+$/.test(locationFromBackend)) {
        try {
          const displayName = getDisplayNameFromId(locationFromBackend);
          locationValue = {
            locationId: locationFromBackend,
            name: displayName
          };
        } catch (error) {
          console.error('Error converting location ID to name:', error);
        }
      }
      
      const query = {
        keyword: item.query?.keywords || '',
        location: locationValue,
        jobType: item.query?.jobType || '',
        experience: item.query?.experience || '',
        salary: item.query?.salary || { min: null, max: null }
      };

      return {
        id: item._id,
        title: item?.query?.keywords || 'Untitled Search',
        query,
        createdAt: new Date(item.createdAt).toLocaleDateString(),
        matchedCandidateCount: item.matchedCandidateCount || 0,
        lastUsed: item.lastUsed,
        isBookmarked: item.isBookmarked || false,
        status: item.status || 'active'
      };
    });
  };

  // const fetchSearches = async (page = 1, pageSize = 10) => {
  //   try {
  //     const recentResponse = await recentSearchAPI.getList(`type=recent&platform=${PLATFORM}&page=${page}&limit=${pageSize}`);
  //     if (recentResponse?.data) {
  //       setRecentSearches({
  //         data: transformSearchData(recentResponse.data),
  //         pagination: recentResponse.pagination || {
  //           total: 0,
  //           page: 1,
  //           limit: pageSize,
  //           pages: 1,
  //           hasMore: false
  //         }
  //       });
  //     }

  //     const bookmarkedResponse = await bookmarkedSearchAPI.getList(`type=bookmarked&platform=${PLATFORM}`);

  //     if (bookmarkedResponse?.data) {
  //       setBookmarkedSearches(transformSearchData(bookmarkedResponse.data));
  //     }

  //     const archivedResponse = await archivedSearchAPI.getList(`type=archived&platform=${PLATFORM}`);

  //     if (archivedResponse?.data) {
  //       setArchivedSearches(transformSearchData(archivedResponse.data));
  //     }
  //   } catch (error) {
  //     console.error('Error fetching searches:', error);
  //   }
  // };
// Update the fetchSearches function to accept a searchTerm parameter
const fetchSearches = async (page = 1, pageSize = 10, searchTerm = '') => {
  try {
    if (searchTerm && searchTerm.trim() !== '') {
      const searchResponse = await searchAPI.getList(
        `platform=${PLATFORM}&query=${encodeURIComponent(searchTerm)}&page=${page}&limit=${pageSize}`
      );
      
      if (searchResponse?.data) {
        setRecentSearches({
          data: transformSearchData(searchResponse.data),
          pagination: searchResponse.pagination || {
            total: 0,
            page: 1,
            limit: pageSize,
            pages: 1,
            hasMore: false
          }
        });
      }
     
    } else {
      const recentResponse = await recentSearchAPI.getList(
        `type=recent&platform=${PLATFORM}&page=${page}&limit=${pageSize}`
      );
      
      if (recentResponse?.data) {
        setRecentSearches({
          data: transformSearchData(recentResponse.data),
          pagination: recentResponse.pagination || {
            total: 0,
            page: 1,
            limit: pageSize,
            pages: 1,
            hasMore: false
          }
        });
      }

      const bookmarkedResponse = await bookmarkedSearchAPI.getList(`type=bookmarked&platform=${PLATFORM}`);
      if (bookmarkedResponse?.data) {
        setBookmarkedSearches(transformSearchData(bookmarkedResponse.data));
      }

      const archivedResponse = await archivedSearchAPI.getList(`type=archived&platform=${PLATFORM}`);
      if (archivedResponse?.data) {
        setArchivedSearches(transformSearchData(archivedResponse.data));
      }
    }
  } catch (error) {
    console.error('Error fetching searches:', error);
  }
};
  const addRecentSearch = async (searchQuery) => {
    try {
      
      let location = searchQuery.query?.location || '';
      if (location && typeof location === 'object') {
        if (location.locationId) {
          location = location.locationId;
        } else if (location.id) {
          location = location.id;
        } else if (location.name) {
          location = location.name;
        }
        location = String(location);
      }
      
      const formattedData = {
        platform: PLATFORM,
        queryParams: {
          keywords: searchQuery.query?.keyword || searchQuery.title || '',
          location: location,
          jobType: searchQuery.query?.jobType || '',
          experience: searchQuery.query?.experience || '',
          salary: searchQuery.query?.salary || { min: null, max: null },
          matches: searchQuery.matches || 0
        }
      };

      const response = await addSearchAPI.post(formattedData);

      if (response?.error) {
        throw new Error(response.error);
      }

      await fetchSearches();
    } catch (error) {
      console.error('Error saving recent search:', error);
      throw error;
    }
  };

  const bookmarkSearch = async (searchQuery) => {
    try {
      if (searchQuery.id) {
        const response = await bookmarkedSearchAPI.put({}, `${searchQuery.id}/toggle-bookmark`);

        if (response?.error) {
          throw new Error(response.error);
        }

        await fetchSearches();
        return response;
      } else {
        let location = searchQuery.query?.location || '';
        if (location && typeof location === 'object') {
          if (location.locationId) {
            location = location.locationId;
          } else if (location.id) {
            location = location.id;
          } else if (location.name) {
            location = location.name;
          }
          location = String(location);
        }
        
    
        const formattedData = {
          platform: PLATFORM,
          queryParams: {
            keywords: searchQuery.query?.keyword || searchQuery.title || '',
            location: location,
            jobType: searchQuery.query?.jobType || '',
            experience: searchQuery.query?.experience || '',
            salary: searchQuery.query?.salary || { min: null, max: null },
            matches: searchQuery.matches || 0,
            isBookmarked: true
          }
        };

 
        const response = await addSearchAPI.post(formattedData);

        if (response?.error) {
          throw new Error(response.error);
        }

        await fetchSearches();
        return response;
      }
    } catch (error) {
      console.error('Error bookmarking search:', error);
      throw error;
    }
  };

  const deleteSearch = async (searchId) => {
    try {
      await deleteSearchAPI.deletee(`${searchId}`);
      await fetchSearches();
    } catch (error) {
      console.error('Error deleting search:', error);
      throw error;
    }
  };

  const toggleArchive = async (searchId) => {
    try {
      if (!searchId) {
        throw new Error('Search ID is required');
      }
      
      const response = await archivedSearchAPI.put({}, `${searchId}/toggle-archive`);
      
      if (response?.error) {
        throw new Error(response.error);
      }
      
      await fetchSearches();
      return response;
    } catch (error) {
      console.error('Error toggling archive status:', error);
      throw error;
    }
  };

  const updateSearch = async (searchId, updatedSearch) => {
    try {
      if (!searchId) {
        throw new Error('Search ID is required');
      }

      // Extract location if it's an object from LocationSelect component
      let location = updatedSearch.location || '';
      if (location && typeof location === 'object') {
        // LocationSelect returns objects with locationId and name properties
        if (location.locationId) {
          // Preserve the full object in our system, but send the ID to the API
          location = {
            locationId: location.locationId,
            name: location.name || ''
          };
        } else if (location.id) {
          location = {
            locationId: location.id,
            name: location.name || ''
          };
        }
      }
      
      // Format data for the API - extract locationId if available
      const locationForAPI = typeof location === 'object' && location.locationId ? 
        location.locationId : 
        location;
      
      const formattedData = {
        platform: PLATFORM,
        queryParams: {
          keywords: updatedSearch.keyword || '',
          location: locationForAPI, // Send locationId or string to API
          jobType: updatedSearch.jobType || '',
          experience: updatedSearch.experience || '',
          salary: {
            min: updatedSearch.minSalary || null,
            max: updatedSearch.maxSalary || null
          },
          radius: updatedSearch.radius || '30'
        },
        title: updatedSearch.title || ''
      };

      const response = await updateSearchAPI.put(formattedData, `${searchId}`);
      
      if (response?.error) {
        throw new Error(response.error);
      }
      
      await fetchSearches();
      return response;
    } catch (error) {
      console.error('Error updating search:', error);
      throw error;
    }
  };

  useEffect(() => {
    fetchSearches();
  }, []);

  return {
    recentSearches,
    bookmarkedSearches,
    archivedSearches,
    addRecentSearch,
    bookmarkSearch,
    deleteSearch,
    toggleArchive,
    updateSearch,
    refreshSearches: fetchSearches,
    loading: recentSearchAPI.loading || bookmarkedSearchAPI.loading || archivedSearchAPI.loading || updateSearchAPI.loading,
    error: recentSearchAPI.error || bookmarkedSearchAPI.error || archivedSearchAPI.error || updateSearchAPI.error,
    success: recentSearchAPI.success || bookmarkedSearchAPI.success || archivedSearchAPI.success || updateSearchAPI.success
  };
};
